import { useQuery } from '@tanstack/react-query';
import { queryKeys } from 'core/api/queryKeys';
import { humanizeApiError } from 'core/errors/mapFieldErrors';
import { useI18n } from 'core/i18n/I18nProvider';
import { companyApi } from 'features/company/company.api';
import { useCompany } from 'features/company/CompanyContext';
import { Card, ErrorState, PageHeader, Skeleton } from 'shared/components/Feedback';
import { PageMeta } from 'shared/components/PageMeta';
import { formatNumber } from 'shared/utilities/format';

export function CompanyCreditsPage() {
  const { t } = useI18n();
  const { companyId } = useCompany();
  const creditsQuery = useQuery({
    queryKey: queryKeys.companyCredits,
    queryFn: () => companyApi.creditsSummary(companyId),
  });
  const entitlementsQuery = useQuery({
    queryKey: queryKeys.companyEntitlements,
    queryFn: () => companyApi.entitlements(companyId, { page: 1, per_page: 50 }),
  });

  if (creditsQuery.isLoading || entitlementsQuery.isLoading) {
    return <Skeleton rows={6} />;
  }
  if (creditsQuery.isError) {
    return <ErrorState title={humanizeApiError(creditsQuery.error, t)} onRetry={() => creditsQuery.refetch()} retryLabel={t('actions.retry')} />;
  }

  const credits = creditsQuery.data?.credits || {};
  const entitlements = entitlementsQuery.data?.items || [];
  return (
    <div className="company-credits-page">
      <PageMeta titleKey="meta.companyCredits" />
      <PageHeader title={t('company.credits.title')} description={t('company.credits.body')} />
      <div className="stat-grid">
        <Card className="stat-card"><p className="kicker">{t('company.credits.total')}</p><p className="stat-value">{formatNumber(credits.total_credits ?? 0, 'en')}</p></Card>
        <Card className="stat-card"><p className="kicker">{t('company.credits.available')}</p><p className="stat-value">{formatNumber(credits.available_credits ?? 0, 'en')}</p></Card>
        <Card className="stat-card"><p className="kicker">{t('company.credits.reserved')}</p><p className="stat-value">{formatNumber(credits.reserved_credits ?? 0, 'en')}</p></Card>
        <Card className="stat-card"><p className="kicker">{t('company.credits.consumed')}</p><p className="stat-value">{formatNumber(credits.consumed_credits ?? 0, 'en')}</p></Card>
      </div>
      <Card>
        <h2>{t('company.credits.entitlements')}</h2>
        {entitlements.length === 0 ? <p className="muted">{t('company.credits.empty')}</p> : (
          <div className="record-table">
            {entitlements.map((entitlement) => (
              <article key={entitlement.id} className="record-row">
                <strong>{entitlement.plan_name || entitlement.id}</strong>
                <p>{t('company.credits.availableCount', { count: entitlement.available_credits ?? 0 })}</p>
                <p className="muted">{entitlement.status_label || entitlement.status}</p>
              </article>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
